/**
 * ###################################################
 *
 * The concept of this encryptedStorage is following :
 *
 *   Only store an encrytion key in the SecureStorage and store the data encrypted with this key in another location. In Detail:
 *
 * - Generate a Radom key using expo-crypto and put the key in SecureStorage (fallback to LocalStorage on the Web).
 *
 * - AES Encrypt all data with this key. This allows us to securly put the data in an unancrypted storage location.
 *
 * - For the data storage we use on
 *
 * iOS: AsyncStorage because it allows us to turn off the iCloud Backup synchronization (see https://react-native-async-storage.github.io/async-storage/docs/advanced/backup)
 *
 *     Android: We Store the Data in Expo-File-System (because of the 6mb Size Limit of AsyncStorage on Android see https://react-native-async-storage.github.io/async-storage/docs/limits )
 *
 *     Web: localforage. We store encrypted the data directly as ArrayBuffer into indexed db, this makes the encryption even faster as there is no need to convert it to string.
 *
 *     It can be used either as persistence Layer for EasyPeasy stores (import createStorage from './encryptedStorage') or used directly as Key-Value Storage (FileSystemStorage.setItem(key,value,directory).
 *
 * ###################################################
 */

import constants from '@/config/constants';
import { logError } from '@/utils/logHandling';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  getRandomBytes,
  digestStringAsync,
  CryptoDigestAlgorithm,
} from 'expo-crypto';
import { CryptoEncoding } from 'expo-crypto';
import { getItemAsync, setItemAsync, deleteItemAsync } from 'expo-secure-store';
import localforage from 'localforage';
import { Alert, Platform } from 'react-native';
import aesHelperMobile from './encryption/aesHelperMobile';
import { aesGcmDecrypt, aesGcmEncrypt } from './encryption/aesHelperWeb';
import { FileSystemStorage } from './file_system_storage/expoFileSystemStorage';
import {
  loadDeviceTokenDataWeb,
  saveDeviceTokenDataWeb,
} from '@/utils/secureDeviceToken';

const isAndroid8AndAbove = Platform.OS === 'android' && Platform.Version >= 26;

const majorVersionIOS = parseInt(Platform.Version?.toString(), 10);
const isIOS13AndAbove = Platform.OS === 'ios' && majorVersionIOS >= 13;

let encryptionKey: null | string = null;
const ALWAYS_PERSIST_KEYS = [
  'hasSeenNewTemplateModal',
  'newNavAnimationCountButton',
  'newNavAnimationCountNav',
];

//const HAS_SUBTLE_CRYPTO_WEB = false;
const HAS_SUBTLE_CRYPTO_WEB =
  window && window.crypto && window.crypto.subtle !== undefined;
const HAS_NATIVE_AES_GCM_CRYPTO =
  !constants.isRunningInExpoGo &&
  (Platform.OS === 'ios' || Platform.OS === 'android') &&
  (isAndroid8AndAbove || isIOS13AndAbove);

let encryptionKeyName: string;
if (HAS_NATIVE_AES_GCM_CRYPTO) {
  encryptionKeyName = 'EncryptionKeyNative';
} else {
  encryptionKeyName = 'y';
}

let memoryStorage: Record<string, unknown> = {};

const ENCRYPTION_ENABLED = !constants.isRunningInExpoGo;
if (
  ENCRYPTION_ENABLED &&
  !HAS_NATIVE_AES_GCM_CRYPTO &&
  !HAS_SUBTLE_CRYPTO_WEB
) {
  let errorMessage;
  if (Platform.OS === 'web') {
    errorMessage =
      'Fehler: Die Verschlüsselung der Daten funktioniert in Ihrem Browser nicht. Die App wird jetzt geschlossen, um Ihre Daten zu schützen';
  } else {
    errorMessage =
      'Fehler: Die Verschlüsselung der Daten funktioniert auf Ihrem Gerät nicht. Die App wird jetzt geschlossen, um Ihre Daten zu schützen';
  }
  displayErrorAlert(errorMessage, 'Verschlüsselungsfehler');
}

function displayErrorAlert(text: string, titleMobileOnly: string): void {
  if (Platform.OS === 'web') {
    alert(text);
    throw new Error(text);
  } else {
    Alert.alert(
      titleMobileOnly,
      text,
      [
        {
          text: 'OK',
          onPress: () => {
            throw new Error(text);
          },
        },
      ],
      { cancelable: false },
    );
  }
}

export default () => {
  const noop = (...args: any[]) => null;

  const getEncryptionKey = async () => {
    if (encryptionKey == null) {
      let encryptionKeyValue;
      if (Platform.OS === 'web') {
        encryptionKeyValue = window.localStorage.getItem(encryptionKeyName);
      } else {
        encryptionKeyValue = await getItemAsync(encryptionKeyName);
      }

      if (!encryptionKeyValue) {
        encryptionKeyValue = await generateRandomEncryptionKey();
        if (Platform.OS === 'web') {
          window.localStorage.setItem(encryptionKeyName, encryptionKeyValue);
        } else {
          await setItemAsync(encryptionKeyName, encryptionKeyValue);
        }
      }
      encryptionKey = encryptionKeyValue;
    }

    return encryptionKey;
  };

  return {
    async getItem(key: string, callback = noop) {
      const stayLoggedIn = await JSON.parse(
        (await AsyncStorage.getItem('stayLoggedIn')) ?? 'null',
      );

      const shouldPersistAlways = ALWAYS_PERSIST_KEYS.includes(key);
      if (
        stayLoggedIn === false &&
        !shouldPersistAlways &&
        key !== '[encryptedStorage][0]'
      ) {
        callback(null, memoryStorage[key]);
        return memoryStorage[key];
      } else {
        try {
          let encryptedValue: string | null;
          let decryptedString: string | null;
          if (Platform.OS === 'web') {
            encryptedValue = await localforage.getItem(key);
          } else if (Platform.OS === 'ios') {
            encryptedValue = await AsyncStorage.getItem(key);
          } else if (Platform.OS === 'android') {
            encryptedValue = await FileSystemStorage.getItem(key);
          } else {
            throw new Error(`unsupported OS: ${Platform.OS}`);
          }
          if (!ENCRYPTION_ENABLED || key !== '[encryptedStorage][0]') {
            // on mobile, we need to parse the unencrypted data to an object, as they are stored as strings.
            if (typeof encryptedValue !== 'object') {
              try {
                encryptedValue = JSON.parse(encryptedValue);
              } catch (error) {
                logError(
                  'Encrypted value is not a JSON string',
                  error as Error,
                );
              }
            }
            callback(null, encryptedValue);
            return encryptedValue;
          } else {
            if (encryptedValue) {
              try {
                const secretKey = await getEncryptionKey();

                if (HAS_SUBTLE_CRYPTO_WEB) {
                  decryptedString = await aesGcmDecrypt(
                    encryptedValue,
                    secretKey,
                    key,
                  );
                } else if (HAS_NATIVE_AES_GCM_CRYPTO) {
                  decryptedString = await aesHelperMobile.decrypt(
                    encryptedValue,
                    secretKey,
                  );
                }
              } catch (error) {
                logError(`error during decryption of ${key}`, error);

                // ERROR DURING DECRYPTION -> CLEAR PERSISTED DATA AND KEY
                if (Platform.OS === 'web') {
                  await localforage.removeItem(key);
                  window.localStorage.removeItem(encryptionKeyName);
                } else if (Platform.OS === 'ios') {
                  await AsyncStorage.removeItem(key);
                  await deleteItemAsync(encryptionKeyName);
                } else if (Platform.OS === 'android') {
                  await FileSystemStorage.removeItem(key);
                  await deleteItemAsync(encryptionKeyName);
                } else {
                  throw new Error(`unsupported Operating System`);
                }
                decryptedString = '{}';
                encryptionKey = null;
                //throw new Error(`Could not decrypt state: ${err.message}`);
              }
            } else decryptedString = encryptedValue;
            try {
              decryptedString = JSON.parse(decryptedString ?? 'null');
            } catch (error) {
              logError('Decrypted string was not an object', error as Error);
            }
            callback(null, decryptedString);
            return decryptedString;
          }
        } catch (error) {
          // error when loading sensible data -> clear all data from persistance and memory and delete the encryption key
          if (Platform.OS === 'web') {
            await localforage.removeItem(key);
            window.localStorage.removeItem(encryptionKeyName);
          } else if (Platform.OS === 'ios') {
            await AsyncStorage.removeItem(key);
            await deleteItemAsync(encryptionKeyName);
          } else if (Platform.OS === 'android') {
            await FileSystemStorage.removeItem(key);
            await deleteItemAsync(encryptionKeyName);
          }
          encryptionKey = null;
          logError(`error when loading encrypted data from ${key}`, error);
          displayErrorAlert(
            'Fehler beim Laden der verschlüsselten Daten. Bitte starten Sie die App neu.',
            'Fehler',
          );
          throw error;
        }
      }
    },

    async setItem(key: string, value: any, callback = noop) {
      const stayLoggedIn = await JSON.parse(
        (await AsyncStorage.getItem('stayLoggedIn')) ?? 'null',
      );

      const shouldPersistAlways = ALWAYS_PERSIST_KEYS.includes(key);
      if (stayLoggedIn === false && !shouldPersistAlways) {
        // only put keys that are not the Easy Peasy storage encryptedStorage[0] as it is never read (already stored in memory by redux)
        if (key !== '[encryptedStorage][0]') {
          memoryStorage[key] = value;
        }
        callback(null);
        return;
      }

      try {
        let encryptedValue: string = value;

        if (ENCRYPTION_ENABLED && key === '[encryptedStorage][0]') {
          if (value) {
            //console.time('stringifying');
            value = JSON.stringify(value);
            //console.timeEnd('stringifying');

            const secretKey = await getEncryptionKey();

            if (HAS_SUBTLE_CRYPTO_WEB) {
              encryptedValue = await aesGcmEncrypt(value, secretKey, key);
            } else if (HAS_NATIVE_AES_GCM_CRYPTO) {
              //console.time("encryption calculation using native AES GCM");

              encryptedValue = await aesHelperMobile.encrypt(value, secretKey);
              //console.timeEnd("encryption calculation using native AES GCM");
            }
          }
        } else {
          if (typeof encryptedValue === 'object' && Platform.OS !== 'web') {
            encryptedValue = JSON.stringify(encryptedValue);
          }
        }
        if (typeof encryptedValue === 'boolean') {
          encryptedValue = JSON.stringify(encryptedValue);
        }
        //console.time('saving data to storage');
        if (Platform.OS === 'web') {
          await localforage.setItem(key, encryptedValue);
        } else if (Platform.OS === 'ios') {
          await AsyncStorage.setItem(key, encryptedValue);
        } else if (Platform.OS === 'android') {
          await FileSystemStorage.setItem(key, encryptedValue);
        } else {
          throw new Error(`unsupported OS: ${Platform.OS}`);
        }
        //console.timeEnd('saving data to storage');

        //console.timeEnd('🔒ENCRYPTION + PERSIST 🔒');

        callback(null);
      } catch (error) {
        callback(error);
        throw error;
      }
    },

    async removeItem(key: string, callback = noop) {
      const stayLoggedIn = await JSON.parse(
        (await AsyncStorage.getItem('stayLoggedIn')) ?? 'null',
      );
      if (stayLoggedIn === false) {
        delete memoryStorage.key;
        callback(null);
      } else {
        try {
          if (Platform.OS === 'web') {
            await localforage.removeItem(key);
          } else if (Platform.OS === 'ios') {
            await AsyncStorage.removeItem(key);
          } else if (Platform.OS === 'android') {
            await FileSystemStorage.removeItem(key);
          } else {
            throw new Error(`unsupported OS: ${Platform.OS}`);
          }
          callback(null);
        } catch (error) {
          callback(error);
          throw error;
        }
      }
    },

    async listItems(callback = noop) {
      let result;
      const stayLoggedIn = await JSON.parse(
        (await AsyncStorage.getItem('stayLoggedIn')) ?? 'null',
      );
      if (stayLoggedIn === false) {
        result = Object.keys(memoryStorage ?? {});
        callback(null, result);
        return result;
      } else {
        try {
          if (Platform.OS === 'web') {
            result = await localforage.keys();
          } else if (Platform.OS === 'ios') {
            result = await AsyncStorage.getAllKeys();
          } else if (Platform.OS === 'android') {
            result = await FileSystemStorage.getAllKeys();
          } else {
            throw new Error(`unsupported OS: ${Platform.OS}`);
          }
          callback(null, result);
          return result;
        } catch (error) {
          callback(error);
          throw error;
        }
      }
    },

    async clear(callback = noop) {
      const stayLoggedIn = await JSON.parse(
        (await AsyncStorage.getItem('stayLoggedIn')) ?? 'null',
      );
      const hasSeenNewTemplateModal = await this.getItem(
        'hasSeenNewTemplateModal',
      );
      const newNavAnimationCountNav = await this.getItem(
        'newNavAnimationCountNav',
      );
      const newNavAnimationCountButton = await this.getItem(
        'newNavAnimationCountButton',
      );
      if (stayLoggedIn === false) {
        memoryStorage = {};
        callback(null);
      } else {
        try {
          encryptionKey = null;
          if (Platform.OS === 'web') {
            await loadDeviceTokenDataWeb();
            window.localStorage.clear();
            await localforage.clear();
            await saveDeviceTokenDataWeb();
          } else if (Platform.OS === 'ios') {
            await deleteItemAsync(encryptionKeyName);
            await AsyncStorage.clear();
          } else if (Platform.OS === 'android') {
            await deleteItemAsync(encryptionKeyName);
            await FileSystemStorage.clear();
          } else {
            throw new Error(`unsupported OS: ${Platform.OS}`);
          }

          callback(null);
        } catch (error) {
          callback(error);
          throw error;
        }
      }

      await this.setItem('hasSeenNewTemplateModal', hasSeenNewTemplateModal);
      await this.setItem(
        'newNavAnimationCountNav',
        JSON.stringify(newNavAnimationCountNav || 0),
      );
      await this.setItem(
        'newNavAnimationCountButton',
        JSON.stringify(newNavAnimationCountButton || 0),
      );
    },
    /***
     * Destroys the currently used encryption key to make all persisted data unusable,
     * then generates a new encryption key for the next session
     *
     * @param callback
     * @returns {Promise<void>}
     */
    async destroyEncryptionKey(callback = noop) {
      encryptionKey = null;
      if (Platform.OS === 'web') {
        window.localStorage.removeItem(encryptionKeyName);
      } else {
        await deleteItemAsync(encryptionKeyName);
      }
      await getEncryptionKey();
      callback(null);
    },
  };
};

async function generateRandomEncryptionKey() {
  let encryptionKeyValue;
  // generating 64 cryptographic strong random bytes
  encryptionKeyValue = getRandomBytes(64);
  // convert those bytes to String
  encryptionKeyValue = String.fromCharCode.apply(null, encryptionKeyValue);
  // has the string with SHA256 to use it ase base64 AES key
  encryptionKeyValue = await digestStringAsync(
    CryptoDigestAlgorithm.SHA256,
    encryptionKeyValue,
    { encoding: CryptoEncoding.BASE64 },
  );
  return encryptionKeyValue;
}
